/**
 * Page-2 donuts (Asset Allokation, Marktkapitalisierung) drawn as vector paths.
 *
 * The earlier donut was a Chart.js canvas captured as PNG and placed at 121 pt: on a
 * dpr-1 screen that is 114 dpi, and the 11 px slice labels arrived as 5 pt raster text
 * with a black outline — SQUAD found them hard to read in print (weekly 25.09.2026).
 * Here the ring is geometry both renderers draw natively (SVG in the preview, react-pdf
 * Svg in the PDF), and the values move into the legend as real text.
 *
 * Since SQUAD chose E1 (15.10.2026) this is the only donut: the canvas version is no longer
 * selected, see normalizeSquadPieStyle.
 */

import { SQUAD_TYPOGRAPHY_PDF } from './pageHeaderTokens';

/** Segoe UI, the embedded legend font: hhea ascent and cap height as shares of the font size. */
export const SQUAD_SEGOE_UI_METRICS = { ascent: 1.0791, capHeight: 0.7002 } as const;

/** Sizes in PDF points; the preview multiplies by SQUAD_VECTOR_PIE_PX_PER_PT. */
export const SQUAD_VECTOR_PIE = {
  /**
   * Ring diameter — the ~77 pt of ring the canvas donut showed inside its 121 pt frame.
   * The slot is 182 pt; ring + gap take 83, the legend keeps 99: mark 5.6, value 18 and two
   * 3 pt gaps leave 69 for the label (65 with the 22 pt column) — every market-cap label fits on two lines
   * ("(<0,1 Mrd. EUR)" is 55.8 pt in Segoe UI 8 pt).
   */
  diameterPt: 77,
  /** Hole as share of the outer radius — the 58 % cutout the Chart.js donut used. */
  cutout: 0.58,
  /** White joint between slices, so neighbouring gold shades stay apart in print. */
  separatorPt: 0.75,
  /** Space between ring and legend. */
  gapPt: 6,
  /**
   * Legend colour mark, exactly as tall as the figures: one cap height of the 8 pt legend
   * text, 5.6 pt (Benjamin 23.10.2026 — the 6 pt mark hung 1.2 pt above the digits).
   */
  markPt: SQUAD_TYPOGRAPHY_PDF.bar * SQUAD_SEGOE_UI_METRICS.capHeight,
  /**
   * Mark offset from the top of its row, so it ends on the baseline: react-pdf sets the
   * first baseline one ascent below the text's top (measured 8.63 pt at 8 pt).
   */
  markTopPt: SQUAD_TYPOGRAPHY_PDF.bar * (SQUAD_SEGOE_UI_METRICS.ascent - SQUAD_SEGOE_UI_METRICS.capHeight),
  /**
   * Value column in front of the label (SQUAD 22.10.2026: closer to the mark, as in E1).
   * Pies have at most two digits: "<1%" in Segoe UI Bold 8 pt is 17.20 pt, "99%" 16.14 pt.
   */
  valueColumnPt: 18,
  /** Only for a legend with a four-character value ("100%" = 20.74 pt). */
  valueColumnWidePt: 22,
} as const;

/** Preview page is 794 px for the PDF's 595.28 pt. */
export const SQUAD_VECTOR_PIE_PX_PER_PT = 794 / 595.28;

/**
 * `vector-plain` = Ring ohne Zahlen, Wert vor dem Text in der Legende — seit 15.10.2026
 * die einzige Darstellung (SQUAD-Wahl E1, siehe normalizeSquadPieStyle). `vector` = Ring
 * mit Zahlen (squadDonutLabels, Weekly 02.10.2026) und `raster` = alter Canvas-Weg werden
 * nicht mehr ausgewählt.
 */
export type SquadPieStyle = 'vector' | 'vector-plain' | 'raster';

/**
 * SQUAD hat sich am 15.10.2026 für E1 entschieden: Ring ohne Zahlen, Wert vor dem Text
 * in der Legende („■ 52% Aktien“) — für alle Factsheets, ohne Umschalter. Deshalb führt
 * jeder gespeicherte Wert (auch 'vector' und 'raster' aus älteren Ständen) zu
 * 'vector-plain'. Nur im Mapper, siehe Arbeitsanweisung Abschnitt 1.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- callers still hand in the stored value
export function normalizeSquadPieStyle(_style: unknown): SquadPieStyle {
  return 'vector-plain';
}

/** Only an explicit 'raster' goes back to the canvas donut; the mapper never stores it. */
export const isSquadRasterPie = (style: unknown): boolean => style === 'raster';

export type SquadDonutSegment = {
  /** SVG path data for one ring segment. */
  d: string;
  color: string;
  /** Index into the input values — legend and slice share it. */
  index: number;
};

const FULL_TURN = Math.PI * 2;

/** 0 rad = 12 o'clock, clockwise — where Chart.js starts its doughnut. */
const pointAt = (cx: number, cy: number, r: number, angle: number) => ({
  x: cx + r * Math.sin(angle),
  y: cy - r * Math.cos(angle),
});

const fmt = (n: number) => Number(n.toFixed(3)).toString();

function ringSegmentPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  a0: number,
  a1: number,
): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const o0 = pointAt(cx, cy, outerR, a0);
  const o1 = pointAt(cx, cy, outerR, a1);
  const i1 = pointAt(cx, cy, innerR, a1);
  const i0 = pointAt(cx, cy, innerR, a0);
  return [
    `M ${fmt(o0.x)} ${fmt(o0.y)}`,
    `A ${fmt(outerR)} ${fmt(outerR)} 0 ${large} 1 ${fmt(o1.x)} ${fmt(o1.y)}`,
    `L ${fmt(i1.x)} ${fmt(i1.y)}`,
    `A ${fmt(innerR)} ${fmt(innerR)} 0 ${large} 0 ${fmt(i0.x)} ${fmt(i0.y)}`,
    'Z',
  ].join(' ');
}

/**
 * One path per non-empty slice, in input order, starting at 12 o'clock clockwise.
 * Negative and non-numeric values count as 0; a slice covering the whole ring is split
 * in two halves, because an SVG arc whose start and end coincide draws nothing.
 */
export function squadDonutSegments(
  values: number[],
  colors: string[],
  geom: { cx: number; cy: number; outerR: number; innerR: number },
  /** Drehung in rad (0 = 12 Uhr) — die Hybrid-Variante legt die kleinen Segmente nach oben. */
  startAngle = 0,
): SquadDonutSegment[] {
  const clean = values.map((v) => (Number.isFinite(Number(v)) ? Math.max(Number(v), 0) : 0));
  const total = clean.reduce((s, v) => s + v, 0);
  if (total <= 0 || colors.length === 0) return [];

  const { cx, cy, outerR, innerR } = geom;
  const out: SquadDonutSegment[] = [];
  let angle = startAngle;
  clean.forEach((value, index) => {
    if (value <= 0) return;
    const sweep = (value / total) * FULL_TURN;
    const color = colors[index % colors.length];
    if (sweep >= FULL_TURN - 1e-9) {
      out.push({ d: ringSegmentPath(cx, cy, outerR, innerR, 0, Math.PI), color, index });
      out.push({ d: ringSegmentPath(cx, cy, outerR, innerR, Math.PI, FULL_TURN), color, index });
    } else {
      out.push({ d: ringSegmentPath(cx, cy, outerR, innerR, angle, angle + sweep), color, index });
    }
    angle += sweep;
  });
  return out;
}

/**
 * White joints between slices, drawn as their own layer over the fills. Stroking each
 * slice instead would paint a small slice over with its neighbours' strokes — a 0.3 %
 * slice became nothing but a seam while the legend still listed it. So a joint is drawn
 * only where both slices beside it are at least `minSliceArcPt` wide along the middle of
 * the ring, and not at all when a single slice fills it.
 */
export function squadDonutSeparators(
  values: number[],
  geom: { cx: number; cy: number; outerR: number; innerR: number },
  minSliceArcPt: number = SQUAD_VECTOR_PIE.separatorPt * 3,
  startAngle = 0,
): string[] {
  const clean = values.map((v) => (Number.isFinite(Number(v)) ? Math.max(Number(v), 0) : 0));
  const total = clean.reduce((s, v) => s + v, 0);
  const slices = clean.filter((v) => v > 0);
  if (total <= 0 || slices.length < 2) return [];

  const { cx, cy, outerR, innerR } = geom;
  const midR = (outerR + innerR) / 2;
  const wide = slices.map((v) => (v / total) * FULL_TURN * midR >= minSliceArcPt);

  const out: string[] = [];
  let angle = startAngle;
  slices.forEach((v, i) => {
    // Joint at the start of slice i, between slice i-1 (wrapping) and slice i
    const prev = (i - 1 + slices.length) % slices.length;
    if (wide[i] && wide[prev]) {
      const a = pointAt(cx, cy, innerR, angle);
      const b = pointAt(cx, cy, outerR, angle);
      out.push(`M ${fmt(a.x)} ${fmt(a.y)} L ${fmt(b.x)} ${fmt(b.y)}`);
    }
    angle += (v / total) * FULL_TURN;
  });
  return out;
}

/** Ring geometry for a square box of `size` — shared by preview and PDF. */
export function squadDonutGeometry(size: number) {
  const outerR = size / 2;
  return { cx: outerR, cy: outerR, outerR, innerR: outerR * SQUAD_VECTOR_PIE.cutout };
}
